import {computed, Directive, inject, OnDestroy, OnInit, Signal} from '@angular/core';
import {TaAppStore} from '../../../state/clcta-store';
import {FullTaDetails, SubmissionExpenseResult, UserTravelItinerary} from '../../../api';
import {Router} from '@angular/router';
import {TaSubmissionDatasource} from '../../../datasources/ta-submission.datasource';
import {ConfirmationWrapperService} from '../../../services/confirmation-wrapper.service';
import {PermissionService} from '../../../services/permission.service';
import {ReturnNavigationService} from '../../../services/return-navigation.service';
import {hasDayTripPayment, taOverpaidAmount, taPaymentComponents, taValueChange} from '../../../util/general.utils';
import {latestItinerariesForSubmission} from '../../../util/itinerary.utils';
import type {DayTripPaymentRow} from './manage-ta-day-trip-payment-table/manage-ta-day-trip-payment-table';

@Directive()
export abstract class ManageTaBaseComponent implements OnInit, OnDestroy {
  store = inject(TaAppStore)
  router = inject(Router)
  taSubmissionDatasource: TaSubmissionDatasource = inject(TaSubmissionDatasource);
  confirmationServiceWrapper = inject(ConfirmationWrapperService);
  permissionService = inject(PermissionService);
  returnNavigation = inject(ReturnNavigationService);

  readonly taSubmission: Signal<FullTaDetails | undefined> = this.store.taSubmission
  readonly taValue: Signal<SubmissionExpenseResult | undefined> = this.store.taValue
  originalTaValue = computed<number>(() => (
    this.taValue()?.originalValue ?? this.taSubmission()?.originalValue ?? 0
  ));
  // null when no pre-payment has been processed (a $0 pre-payment is still one).
  prePaidTaValue = computed<number | null>(() => (
    this.taValue()?.prePaidValue ?? this.taSubmission()?.prePaidValue ?? null
  ));
  currentTaValue = computed<number>(() => (
    this.taValue()?.currentValue ?? this.taSubmission()?.currentValue ?? 0
  ));
  // Current value less day trips. Falls back to the full value so a missing field never reads as overpaid.
  currentFinanceValue = computed<number>(() => (
    this.taValue()?.currentFinanceValue ?? this.taSubmission()?.currentFinanceValue ?? this.currentTaValue()
  ));
  changeInTaValue = computed<number>(() => taValueChange({
    originalValue: this.originalTaValue(),
    currentValue: this.currentTaValue(),
    prePaidValue: this.prePaidTaValue(),
    currentFinanceValue: this.currentFinanceValue(),
  }));
  isOverpaid = computed<boolean>(() => this.changeInTaValue() < 0);
  isPrePaid = computed<boolean>(() => !!this.taSubmission()?.isPrePaid);
  paymentComponents = computed(() => taPaymentComponents({
    currentValue: this.currentTaValue(),
    currentFinanceValue: this.currentFinanceValue(),
    prePaidValue: this.prePaidTaValue(),
    isPrePaid: this.isPrePaid(),
  }));
  prepaymentComponent = computed<number>(() => this.paymentComponents().prepayment);
  dayTripComponent = computed<number>(() => this.paymentComponents().dayTrip);
  acquittedValueChange = computed<number>(() => this.currentTaValue() - this.originalTaValue());
  finalisationPaymentDue = computed<number>(() => this.paymentComponents().finalisationDue);
  // Only a processed pre-payment can be overpaid; a non-prepaid drop is not an overpayment.
  overpaymentMade = computed<number>(() => taOverpaidAmount({
    originalValue: this.originalTaValue(),
    currentValue: this.currentTaValue(),
    prePaidValue: this.prePaidTaValue(),
    currentFinanceValue: this.currentFinanceValue(),
  }));
  // Acquittal approval: no DayTripPayment approval exists yet, so gate on the TA having day trips.
  showPreAcquittalDayTripTable = computed<boolean>(() =>
    this.permissionService.canViewDayTripBreakdown() && this.dayTripComponent() > 0);
  travellersLength = computed<number>(() => (this.taSubmission()?.travellers?.length ?? 0))
  canCancelPrepayment = computed<boolean>(() => this.isPrePaid() && this.permissionService.canCancelPrepayment());

  // allItineraries holds every version; the server judges the current one per traveller.
  // Hosts reading this must call taSubmissionDatasource.getAllItineraries().
  protected latestItineraries = computed<UserTravelItinerary[]>(() =>
    latestItinerariesForSubmission(this.taSubmissionDatasource.allItineraries(), this.taSubmission()?.id));

  showDayTripBreakdown = computed<boolean>(() =>
    hasDayTripPayment(this.taSubmission()) && this.permissionService.canViewDayTripBreakdown());
  dayTripPaymentRows = computed<DayTripPaymentRow[]>(() => {
    const amounts = this.taValue()?.travellerAmounts ?? {};
    const itineraries = this.latestItineraries();
    const rows = (this.taSubmission()?.travellers ?? []).map((traveller): DayTripPaymentRow => {
      const items = itineraries.find(itinerary => itinerary.userTravelId === traveller.travellerId)?.items ?? [];
      const portion = (traveller.travellerId && amounts[traveller.travellerId]) || 0;
      const dayTripAmount = items.reduce((sum, item) => sum + (item.dayTripValue ?? 0), 0);
      return {travellerName: traveller.details?.name ?? '-', portion, dayTripAmount, finalisationAmount: portion - dayTripAmount};
    });

    // Prepaid TAs are single-traveller, so the one row carries the whole pre-payment and owes
    // what's left after it ($0 when overpaid).
    const [row] = rows;
    if (this.isPrePaid() && row) {
      row.prepaymentAmount = this.prepaymentComponent();
      row.finalisationAmount = Math.max(0, row.finalisationAmount - row.prepaymentAmount);
    }
    return rows;
  });

  constructor() {}

  // Lifecycle hooks
  ngOnInit(): void {
    // console.log('ManageTaBaseComponent INIT');
  }

  ngOnDestroy(): void {
    // console.log('ManageTaBaseComponent ONDESTROY');
  }

  confirmCancelPrepayment(processed: boolean): void {
    this.confirmationServiceWrapper.cancelPrepaymentConfirm(this.taSubmission(), processed).subscribe({
      next: () => {},
      error: err => {
        console.error(err);
      },
    });
  }

  goBack(): void {
    this.returnNavigation.navigateBack('/approvals');
  }
}
